import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Form,
    Input,
    Modal,
    Popconfirm,
    Space,
    Table,
    Typography,
} from "antd";
import { useParams } from "react-router-dom";
import { SectionPanelProps } from "./SectionTypes";
import { FacilityTypeRow } from "../../../model/facilities/FacilityTypeRow";
import { CreateFacilityTypeRequest } from "../../../requests/facility/CreateFacilityTypeRequest";
import { UpdateFacilityTypeRequest } from "../../../requests/facility/UpdateFacilityTypeRequest";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";

const { Text } = Typography;

// Settings panel for the per-client facility-type CRUD (FORT-131). Ignores
// the controlled value/onChange props because this section owns a list of
// rows, not a single value — it loads/persists each row directly through
// the settings service rather than bubbling state up to the shell.
export const FacilityTypesPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const service = useClientConfigService();
    const { clientId: clientIdParam } = useParams<{ clientId: string }>();
    const clientId = clientIdParam !== undefined ? Number(clientIdParam) : NaN;

    const [rows, setRows] = useState<FacilityTypeRow[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    const [editingId, setEditingId] = useState<number | 'new' | null>(null);
    const [draftName, setDraftName] = useState<string>('');
    const [saving, setSaving] = useState(false);

    const refresh = useCallback(async () => {
        if (!Number.isFinite(clientId)) return;
        try {
            const next = await service.listFacilityTypes(clientId);
            setRows(next);
            setLoadError(null);
        } catch (err) {
            setLoadError(err instanceof Error ? err.message : 'Failed to load facility types.');
        }
    }, [service, clientId]);

    useEffect(() => {
        if (!Number.isFinite(clientId)) {
            setLoadError('Invalid client id in URL.');
            return;
        }
        setLoadError(null);
        void refresh();
    }, [clientId, refresh]);

    const handleOpenCreate = () => {
        setDraftName('');
        setEditingId('new');
        setActionError(null);
    };

    const handleOpenEdit = (row: FacilityTypeRow) => {
        setDraftName(row.name);
        setEditingId(row.id);
        setActionError(null);
    };

    const handleCloseModal = () => {
        if (saving) return;
        setEditingId(null);
        setActionError(null);
    };

    const handleSave = async () => {
        if (editingId === null) return;
        const trimmed = draftName.trim();
        if (trimmed === '') {
            setActionError('Name is required.');
            return;
        }
        setSaving(true);
        setActionError(null);
        try {
            if (editingId === 'new') {
                const payload: CreateFacilityTypeRequest = { name: trimmed };
                await service.createFacilityType(clientId, payload);
            } else {
                const payload: UpdateFacilityTypeRequest = { name: trimmed };
                await service.updateFacilityType(clientId, editingId, payload);
            }
            setEditingId(null);
            await refresh();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to save facility type.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = useCallback(async (row: FacilityTypeRow) => {
        try {
            await service.deleteFacilityType(clientId, row.id);
            await refresh();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to delete facility type.');
        }
    }, [service, clientId, refresh]);

    const columns = useMemo(() => [
        {
            title: 'Name',
            dataIndex: 'name' as const,
            render: (name: string, row: FacilityTypeRow) => (
                <span data-testid={`ft-row-name-${row.id}`}>{name}</span>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, row: FacilityTypeRow) => (
                <Space>
                    <Button
                        size="small"
                        data-testid={`ft-edit-${row.id}`}
                        onClick={() => handleOpenEdit(row)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete this facility type?"
                        description="Facilities already using this type will block deletion."
                        okText="Delete"
                        cancelText="Cancel"
                        onConfirm={() => handleDelete(row)}
                    >
                        <Button size="small" danger data-testid={`ft-delete-${row.id}`}>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ], [handleDelete]);

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="ft-load-error" />;
    }
    if (rows === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
                <Button
                    type="primary"
                    data-testid="ft-add-btn"
                    onClick={handleOpenCreate}
                >
                    Add facility type
                </Button>
            </Space>
            {actionError && (
                <Alert type="error" message={actionError} data-testid="ft-action-error" />
            )}
            <Table
                rowKey="id"
                dataSource={rows}
                columns={columns}
                pagination={false}
                data-testid="ft-table"
                locale={{ emptyText: 'No facility types yet.' }}
            />
            <Modal
                open={editingId !== null}
                title={editingId === 'new' ? 'Add facility type' : 'Edit facility type'}
                onCancel={handleCloseModal}
                onOk={handleSave}
                okText="Save"
                confirmLoading={saving}
                okButtonProps={{
                    disabled: draftName.trim() === '',
                    'data-testid': 'ft-modal-save',
                } as any}
                cancelButtonProps={{ disabled: saving }}
                destroyOnClose
            >
                <Form layout="vertical" data-testid="ft-form">
                    <Form.Item
                        label="Name"
                        validateStatus={draftName.trim() === '' ? 'error' : undefined}
                        help={draftName.trim() === '' ? 'Name is required.' : undefined}
                    >
                        <Input
                            data-testid="ft-form-name"
                            value={draftName}
                            onChange={(e) => setDraftName(e.target.value)}
                            autoFocus
                            maxLength={100}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );
};
